import moment from "moment";
import {
  MODEL_AVAILABLE_NO_DATE,
  MODEL_AVAILABLE_NO_DATE_SHORT,
  MODEL_AVAILABLE_UNTIL,
  MODEL_AVAILABLE_UNTIL_SHORT,
  MODEL_DEPRECATED_QUESTION_PREFIX,
  MODEL_DEPRECATED_QUESTION_SUFFIX,
  MODEL_RETIRED_ON,
  MODEL_RETIRED_ON_SHORT,
  MODEL_RETIRED_SUFFIX,
  MODEL_RETIRING_NO_DATE,
  MODEL_RETIRING_ON,
  MODEL_SUCCESSOR_PREFIX,
  MODEL_SUCCESSOR_SUFFIX,
} from "../configs";

/** The calendar day of an instant, in UTC — the frame the vendor publishes its dates in. */
const utcDay = (value: string) => moment.utc(value).format("YYYY-MM-DD");

/**
 * How a shutdown date reads against today: before it, past it, or unusable.
 *
 * THREE STATES, NOT A BOOLEAN. A date we cannot parse is neither — "not past" would print
 * "Available until soon", quoting the broken value back as if it were a date, and "past" would
 * retire a live model on a typo. So the caller drops to the dateless wording instead.
 *
 * COMPARED AS UTC CALENDAR DAYS. The vendor serves the model THROUGH the date it published, so the
 * boundary day is still available — and the date arrives as a bare `YYYY-MM-DD` in the vendor's own
 * frame. Parsing that in local time puts it at local midnight, which for anyone east of UTC is the
 * previous day in UTC and retires the model early. Formatting both sides to a UTC day string and
 * comparing those is exact, and is why this does not use moment's `"day"` granularity: that compares
 * in whatever offset each side was parsed with.
 */
function readShutdown(date: string, today?: string): "upcoming" | "past" | "unreadable" {
  if (!moment.utc(date, moment.ISO_8601, true).isValid()) return "unreadable";
  return utcDay(date) < utcDay(today ?? moment.utc().toISOString()) ? "past" : "upcoming";
}

/**
 * What to say about a model's remaining life, or nothing at all.
 *
 * LEADS WITH WHAT IS TRUE NOW, because a deprecated model still runs: every prompt pointed at one
 * keeps answering until the vendor's shutdown date, and the only thing an operator must not do is
 * point a NEW configuration at it. A bare "DEPRECATED" states the vendor's status and leaves the
 * reader to infer the availability — and the inference goes the wrong way, reading as "already
 * gone", which is the one thing it does not mean.
 *
 * `isDeprecated` alone decides whether to speak. The date cannot: a vendor often marks a model
 * deprecated with no date attached, so keying on the date would stay silent on those rows — and
 * silence there is the gap an operator falls into, picking a model the service already knows is
 * ending.
 *
 * ONE FUNCTION FOR BOTH LENGTHS. The grid's cell cannot hold the sentence, but a second wording
 * written where it is rendered would drift from this one, and then two screens would describe one
 * model two ways. `compact` shortens the words around the date; it never drops the date, which is
 * the only part an operator can act on.
 *
 * PAST THE DATE IT STOPS SAYING "AVAILABLE", because by then that is simply false. Nothing removes a
 * model from the picker on its shutdown date — the list comes from the backend's SUPPORTED_LLM_MODELS
 * and no code compares that date to today — so without this the screen would keep offering "Available
 * until" a date that has gone by, which is worse than the "DEPRECATED" this wording replaced.
 *
 * `today` is for tests only. Production reads the clock; a test that read it too would pass until the
 * date arrived and then start failing on its own.
 */
export function modelAvailability(
  model: { isDeprecated?: boolean; shutdownAt?: string },
  { compact = false, today }: { compact?: boolean; today?: string } = {}
): string | undefined {
  if (!model.isDeprecated) return undefined;
  const state = model.shutdownAt ? readShutdown(model.shutdownAt, today) : "unreadable";
  if (state === "past") {
    return compact
      ? `${MODEL_RETIRED_ON_SHORT} ${model.shutdownAt}`
      : `${MODEL_RETIRED_ON} ${model.shutdownAt} ${MODEL_RETIRED_SUFFIX}`;
  }
  if (state === "upcoming") {
    const lead = compact ? MODEL_AVAILABLE_UNTIL_SHORT : MODEL_AVAILABLE_UNTIL;
    return `${lead} ${model.shutdownAt}`;
  }
  // No date, or one that cannot be read: say what we do know rather than quote a broken value back.
  return compact ? MODEL_AVAILABLE_NO_DATE_SHORT : MODEL_AVAILABLE_NO_DATE;
}

/**
 * Whether this model has passed its shutdown date, for the caller that needs to style it differently.
 *
 * Exported alongside the wording rather than re-derived at the call site: a screen deciding "is this
 * red" from its own date comparison would disagree with the sentence `modelAvailability` returned.
 */
export function isModelRetired(model: { isDeprecated?: boolean; shutdownAt?: string }, today?: string): boolean {
  if (!model.isDeprecated || !model.shutdownAt) return false;
  return readShutdown(model.shutdownAt, today) === "past";
}

/**
 * The warning line in the dialog shown when an operator picks a retiring model.
 *
 * STATES THE CONSEQUENCE, because this one is read WHILE CHOOSING rather than while reading. The bar
 * above reports a model already in use, so there "Available until" is the useful frame; here the
 * operator is deciding, and what they need is what happens if they go ahead — the prompt stops
 * working on a date. "<model> is deprecated" named the vendor's label instead, which is the one part
 * of this they cannot act on.
 *
 * Takes the name rather than a model object so the dialog cannot be handed a different model from the
 * one the picker is about to apply.
 */
export function modelRetirementWarning(modelName: string, shutdownAt?: string): string {
  return shutdownAt ? `${modelName} ${MODEL_RETIRING_ON} ${shutdownAt}` : `${modelName} ${MODEL_RETIRING_NO_DATE}`;
}

/**
 * The dialog's question, naming the model.
 *
 * Never "it": the line between the warning and the question is the vendor's prose, which routinely
 * names a DIFFERENT id — the dated snapshot behind an alias — so a pronoun there has two candidate
 * referents on screen and the wrong one is the more prominent.
 */
export function modelRetirementQuestion(modelName: string): string {
  return `${MODEL_DEPRECATED_QUESTION_PREFIX} ${modelName} ${MODEL_DEPRECATED_QUESTION_SUFFIX}`;
}

/**
 * What the vendor says to use instead, or nothing.
 *
 * Undefined rather than a placeholder when the list is empty, which is the common case: most
 * deprecated rows name no successor, and a line saying so would push the actionable part of the
 * dialog further from the eye for no gain.
 */
export function successorAdvice(alternatives?: string[]): string | undefined {
  if (!alternatives?.length) return undefined;
  // "a or b", not "a, b": these are choices, and a comma list reads as a sequence to work through.
  const names = alternatives.length === 1 ? alternatives[0] : alternatives.join(" or ");
  return `${MODEL_SUCCESSOR_PREFIX} ${names} ${MODEL_SUCCESSOR_SUFFIX}`;
}
